import { z } from 'zod'
import type { Alternative, LineItem } from './slips'

const group = z.object({
  groupId: z.string(),
  type: z.string(),
  control: z
    .object({
      type: z.string().optional(),
      none: z.string().optional(),
      regular: z.string().optional(),
      extra: z.string().optional(),
    })
    .passthrough()
    .optional(),
})
const component = z.object({
  id: z.string(),
  type: z.enum(['Fixed', 'Choice']),
  groupId: z.string().optional(),
  groups: z.array(group).default([]),
  options: z.array(z.object({ alternativeItemName: z.string(), groups: z.array(group).default([]) })).optional(),
})
const flowSchema = z.object({
  byMenuType: z.object({
    EatIn: z.object({ components: z.array(component) }).optional(),
    TakeAway: z.object({ components: z.array(component) }).optional(),
  }),
})
export type KitchenComponent = z.infer<typeof component>
export type KitchenFlowIndex = Map<string, KitchenComponent[]>
export function kitchenFlowIndex(
  products: {
    kioskFlow?: unknown
    eatIn?: { id: string } | null
    takeAway?: { id: string } | null
  }[],
): KitchenFlowIndex {
  const index: KitchenFlowIndex = new Map()
  for (const product of products) {
    const parsed = flowSchema.safeParse(product.kioskFlow)
    if (!parsed.success) continue
    for (const [channel, variant] of [
      ['EatIn', product.eatIn],
      ['TakeAway', product.takeAway],
    ] as const) {
      const components = parsed.data.byMenuType[channel]?.components
      if (variant && components) index.set(variant.id, components)
    }
  }
  return index
}
export type KitchenModification = { text: string; tone?: 'extra' | 'none' }
export type KitchenRow = {
  id: string
  name: string
  count: number
  modifications: KitchenModification[]
}
const label = (a: Alternative) => [a.prefix, a.name].filter(Boolean).join(' ')

/** Keep API completion on the original line. Components are a readable breakdown, not new tasks. */
export function kitchenRows(item: LineItem, components: KitchenComponent[] = []): KitchenRow[] {
  const alternatives = (item.alternativeItems ?? []).filter((a): a is Alternative => a != null)
  const componentItems = alternatives.filter((a) => a.kitchenPresentation === 'Component' && a.isChosen !== false && (a.count ?? 1) > 0)
  const rows: KitchenRow[] = componentItems.map((a) => ({
    id: a.id,
    name: a.name ?? a.alternativeItemName ?? '',
    count: (item.count ?? 1) * (a.count ?? 1),
    modifications: [],
  }))
  const parent: KitchenRow = {
    id: item.id,
    name: item.name ?? '',
    count: item.count ?? 1,
    modifications: [],
  }
  const handled = new Set(componentItems.map((a) => a.id))
  for (const owner of components) {
    const selected = componentItems.find((a) => a.alternativeGroupId === owner.groupId)
    const row = owner.type === 'Fixed' ? parent : rows.find((r) => r.id === selected?.id)
    const option = owner.options?.find((o) => o.alternativeItemName === selected?.alternativeItemName)
    // Also consume groups of hidden options. A side's changes must not leak onto the burrito station.
    const allGroups = [...owner.groups, ...(owner.options ?? []).flatMap((o) => o.groups)]
    const activeGroups = [...owner.groups, ...(option?.groups ?? [])]
    for (const g of allGroups) {
      const changes = alternatives.filter((a) => a.alternativeGroupId === g.groupId && !handled.has(a.id))
      changes.forEach((a) => {
        handled.add(a.id)
      })
      if (!row || !activeGroups.some((active) => active.groupId === g.groupId)) continue
      const chosen = changes.filter((a) => a.isChosen && (a.count ?? 1) > 0)
      const displayed = g.type === 'Ingredient' && chosen.length ? chosen : changes
      row.modifications.push(
        ...displayed.map((a): KitchenModification => {
          const text = g.type === 'Ingredient' && a.isChosen ? (a.name ?? a.alternativeItemName ?? '') : label(a)
          // Portion semantics come from the configured option identity, never its label/prefix.
          if (g.type === 'Ingredient' && g.control?.type === 'Portion' && a.isChosen && (a.count ?? 1) > 0 && a.alternativeItemName != null) {
            if (a.alternativeItemName === g.control.none) return { text, tone: 'none' }
            if (a.alternativeItemName === g.control.extra) return { text, tone: 'extra' }
          }
          return { text }
        }),
      )
    }
  }
  parent.modifications.push(...alternatives.filter((a) => !handled.has(a.id)).map((a) => ({ text: label(a) })))
  // Keep the purchased product as context whenever it owns unmatched modifications.
  return rows.length ? [...(parent.modifications.length ? [parent] : []), ...rows] : [parent]
}
